import { RequestError } from '@vartion/ui'
import type { WatchOptions } from 'vue'

import { userpilotClient } from '~/services/UserpilotClient.ts'
import { type CaseModel, type DataSource, type FormattedCaseModel, type Meta, type Organization } from '~/types.ts'

export function scoreColor(score: number, format = 'hsl') {
  if (format === 'hsl') {
    const color = 120 * (score / 100)

    return `hsl(${color}, 80%, 50%)`
  }

  const red = score < 50 ? 255 : Math.floor(255 - ((score * 2 - 100) * 255) / 100)
  const green = score > 50 ? 255 : Math.floor((score * 2 * 255) / 100)

  return `rgb(${red}, ${green}, 0)`
}

export function formatCountry(code: string) {
  return code ? stores.countries.countriesByAlpha3Code[code.toLowerCase()].name : ''
}

// We cannot use `\b` word boundary because it does not work with for example cyrillic characters.
export function escapeRegExp(string: string) {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&') // `$&` means the whole matched string.
}

export const wordBoundaries = {
  before: '(^|[\\s])',
  after: '(?=[\\s,.’\'”"!?:]|$)',
}

const tokenCharactersRegex = /[^' ',\-/.]/
export function containsOnlyTokenCharacters(string: string) {
  if (string.length === 0) {
    return false
  }

  return !tokenCharactersRegex.test(string)
}

export function adverseTags(data: object) {
  const adverse = stores.initialState.adverse

  const tags = []

  for (const [key, value] of Object.entries(data ?? {})) {
    if (key !== 'narcotics' && value >= adverse.minimum_threshold) {
      tags.push($t(adverse.labels[key] ?? kebab(key)))
    }
  }

  return tags
}

interface Component {
  [key: string]: any
  $children: Component[]
  $options: { [key: string]: any; name: string }
}
export function findChildComponent(component: Component, childName: string): Component | null {
  for (const child of component.$children) {
    if (child.$options.name === childName) {
      return child
    }

    const childComponent = findChildComponent(child, childName)

    if (childComponent) {
      return childComponent
    }
  }

  return null
}

export function blurAll() {
  const tmp = document.createElement('input')
  document.body.appendChild(tmp)
  tmp.focus()
  document.body.removeChild(tmp)
}

export function dataSourceVendor(dataSourceId: number) {
  const dataSource = stores.dataSources.dataSources.find((dataSource: DataSource) => dataSource.id === dataSourceId)

  if (dataSource) {
    const vendor = dataSource.vendor

    if (vendor === 'custom') {
      return $t('data-directory')
    }
    if (vendor === 'default') {
      return 'Insight'
    }

    return vendor
  }

  return $t('not-available')
}

/**
 * Returns true if value matches pattern using SQL ILIKE syntax (% and _).
 */
export function iLike(string: string, pattern: string): boolean {
  const regex = pattern.replace(/%/g, '.*').replace(/_/g, '.')

  const regexPattern = new RegExp(`^${regex}$`, 'i') // Create a case-insensitive regex pattern.

  return regexPattern.test(string)
}

export interface Assignee {
  type: 'user' | 'group'
  value: number | null
}

/**
 * Expanded type for case and policy configuration. Actual `CaseModel` objects do not have this.
 */
export interface AssigneeOrSelf {
  type: 'user' | 'group' | 'self'
  value: number | null
}

export function toAssignee(caseModel: CaseModel | FormattedCaseModel): Assignee {
  if (caseModel.user_id) {
    return { type: 'user', value: caseModel.user_id }
  }

  return { type: 'group', value: caseModel.group_id }
}

/**
 * Recursively searches nested arrays and objects for a string match (case-insensitive).
 */
type RecursiveArray = (RecursiveArray | string | object)[]
export function stringInArray({ array, string, depth = 0, maxDepth = 5 }: { array: RecursiveArray; string: string; depth?: number; maxDepth?: number }): boolean {
  string = string.toLowerCase()

  return array.some((value) => {
    if (typeof value === 'string') {
      return value.toLowerCase().includes(string)
    } else if (Array.isArray(value) && depth < maxDepth) {
      return stringInArray({ array: value, string, depth: depth + 1 })
    } else if (typeof value === 'object' && value !== null && depth < maxDepth) {
      return stringInArray({ array: Object.values(value), string, depth: depth + 1 })
    }

    return false
  })
}

export function getDomain(url: string | URL) {
  return new URL(url).hostname
}

export async function logout() {
  await web.post('logout')
  userpilotClient.destroy()
  window.location.replace(window.location.origin)
}

export function formatCase(item: CaseModel<['clients']>): FormattedCaseModel {
  const formatted: FormattedCaseModel = Object.assign(
    {
      assignee: 'Unassigned',
      group: 'Unassigned',
      riskLabel: null,
    },
    item,
  )

  const user = stores.users.users[item.user_id ?? -1]
  if (user) {
    formatted.assignee = `${user.first_name} ${user.last_name}`
  }

  const group = stores.groups.groups[item.group_id ?? -1]
  if (group) {
    formatted.group = group.name
  }
  formatted.riskLabel = getHighestRisk(item.risk)

  return formatted
}

export function createMeta(): Meta {
  return {
    currentPage: 1,
    from: 0,
    pageCount: 1,
    pageSize: 10,
    to: 10,
    total: 0,
  }
}

export function openExternal(url: string) {
  const a = document.createElement('a')
  a.href = `/redirect/${url}`
  a.target = '_blank'
  a.rel = 'noopener'
  a.click()
  a.remove()
}

export function isOrganizationValid(organization: Organization<['pivot']>) {
  return organization.pivot.deleted_at === null
}

export function isOneTimeCase() {
  return !!api.headers.Authorization
}

export function setDocumentTitle(title: string) {
  document.title = `${title} - ${$env.app_name ?? 'Pascal'}`
}

export function isAbortError(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true
  }

  if (error instanceof RequestError) {
    error = error.message
  }

  if (typeof error === 'string' && (error === 'AbortError' || error.startsWith('Request aborted due to a new request with the same fingerprint'))) {
    return true
  }

  return false
}

export function getWeekDays(): { value: number; label: string }[] {
  const days = []

  for (let index = 1; index <= 7; index++) {
    days.push({
      value: index,
      label: capitalizeFirst(dayjs().isoWeekday(index).format('dddd')),
    })
  }

  return days
}

export function userName(item: { user_id: null | number; user_name?: null | string }) {
  if (item.user_id) {
    return stores.users.users[item.user_id]?.name ?? item.user_name ?? $env.app_name
  }

  return $env.app_name
}

export function restartableWatch(
  sources: MaybeRefOrGetter<unknown> | MaybeRefOrGetter<unknown>[],
  callback: (...args: any[]) => any,
  options: WatchOptions = {},
): { stop: () => void; start: () => void } {
  let watcher = watch(sources, callback, options)

  function stop() {
    watcher.stop()
    options.immediate = false
  }

  function start() {
    watcher = watch(sources, callback, options)
  }

  onUnmounted(stop)

  return { stop, start }
}
